import { Box, Button, Card, CardContent, Container, Grid, Typography } from "@mui/material";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";

import React, { useState } from "react";
import { RequestDemoDialog } from "../components/public/RequestDemoDialog";
import {
  AbsenteesBySectionChart,
  FeeCollectionByGradeChart,
  HeroDashboardPreview,
  OverviewStatTiles,
} from "../components/landing/LandingCharts";

// -------------------- Shared styles --------------------
// One type scale and one section rhythm for the whole page.
const TINT_BG = "#f7f9fc";
const sectionPy = { xs: 7, md: 11 };

const sectionHeadingSx = {
  fontSize: { xs: "1.75rem", md: "2.25rem" },
  fontWeight: 800,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
};

const featureTitleSx = {
  fontSize: { xs: "1.35rem", md: "1.6rem" },
  fontWeight: 700,
  lineHeight: 1.3,
  letterSpacing: "-0.01em",
  color: "#0f172a",
  mb: 1.5,
};

const bodySx = {
  fontSize: { xs: "0.975rem", md: "1.05rem" },
  lineHeight: 1.75,
  color: "text.secondary",
};

const cardSx = {
  height: "100%",
  borderRadius: 3,
  border: "1px solid #e5e7eb",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    borderColor: "#cfe0f5",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
  },
};

const iconBadgeSx = {
  width: 44,
  height: 44,
  borderRadius: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "rgba(25, 118, 210, 0.08)",
  color: "primary.main",
  flexShrink: 0,
};

// Bullet list with check icons; the item text stays exactly as written.
const CheckList = ({ items }: { items: string[] }) => (
  <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 1.25 }}>
    {items.map((item) => (
      <Box component="li" key={item} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <CheckCircleRoundedIcon sx={{ fontSize: 20, color: "primary.main", mt: "3px" }} />
        <Typography sx={{ ...bodySx, color: "#334155" }}>{item}</Typography>
      </Box>
    ))}
  </Box>
);

// Text + visual side by side on desktop, stacked on mobile (text always first on mobile).
const FeatureRow = ({ visual, children, reverse = false }: { visual: React.ReactNode; children: React.ReactNode; reverse?: boolean }) => (
  <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
    <Grid size={{ xs: 12, md: 6 }} order={{ xs: 1, md: reverse ? 2 : 1 }}>
      {children}
    </Grid>
    <Grid size={{ xs: 12, md: 6 }} order={{ xs: 2, md: reverse ? 1 : 2 }}>
      {visual}
    </Grid>
  </Grid>
);

// -------------------- Landing Page --------------------
export default function LandingPage() {

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      <HeroSection />
      <ProductPreviewSection />
      <section id="features">
        <ModulesSection />
      </section>
      <HowItWorks />
      <WhyUs />
      {window.location.href.includes("demo") && <FinalCTA />}
      <Footer />
    </Box>
  );
}

// -------------------- Navbar --------------------

// -------------------- Hero --------------------
function HeroSection() {
  const [openDemo, setOpenDemo] = useState(false);
  return (
    <>
      <RequestDemoDialog open={openDemo} onClose={() => setOpenDemo(false)} />

      <Box
        sx={{
          py: { xs: 7, md: 12 },
          background: "radial-gradient(1200px 500px at 85% -10%, rgba(25,118,210,0.10), transparent 60%), linear-gradient(180deg, #f5f9ff, #ffffff)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.25rem" },
                  fontWeight: 800,
                  lineHeight: 1.12,
                  letterSpacing: "-0.03em",
                  color: "#0f172a",
                  mb: 2.5,
                }}>
                {/* Run Your Entire School From One Simple Platform */}
                {/* Complete School Management, Made Simple with MyCampusPilot */}
                Manage Your School Effortlessly with MyCampusPilot
                {/* Run Your Entire School from One Simple Platform with My Campus Pilot */}
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.05rem", md: "1.25rem" },
                  fontWeight: 600,
                  lineHeight: 1.5,
                  color: "primary.dark",
                  mb: 2,
                }}
              >
                Admissions, Attendance, Fees, Exams, Communication & Reports — All in One Powerful School Management System.
              </Typography>

              <Typography sx={{ ...bodySx, maxWidth: 560 }}>
                My Campus Pilot helps schools digitize daily operations, reduce paperwork, improve staff productivity, and give management full control and visibility across the institution.
              </Typography>

            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <HeroDashboardPreview />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}

function ProductPreviewSection() {
  return (
    <>
      <Box sx={{ py: sectionPy, backgroundColor: "#ffffff" }}>
        <Container maxWidth="lg">
          {/* Section Heading */}
          <Box sx={{ mb: { xs: 6, md: 9 }, textAlign: "center", maxWidth: 680, mx: "auto" }}>
            <Typography variant="h2" sx={{ ...sectionHeadingSx, mb: 1.5 }}>
              Powerful Insights at a Glance
            </Typography>
            <Typography sx={bodySx}>
              Everything school administrators need to stay informed and in control.
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gap: { xs: 8, md: 12 } }}>
            {/* Row 1 */}
            <FeatureRow reverse visual={<OverviewStatTiles />}>
              <Typography variant="h3" sx={featureTitleSx}>
                Complete School Overview
              </Typography>
              <Typography sx={bodySx}>
                Monitor your school’s key operations with ease through a single, intuitive dashboard. Track student strength, keep an eye
                on admissions in progress, manage attendance data, and analyze fee performance all in real-time. Stay informed and make
                data-driven decisions to enhance efficiency and student success.
              </Typography>
            </FeatureRow>

            {/* Row 2 */}
            <FeatureRow visual={<AbsenteesBySectionChart />}>
              <Typography variant="h3" sx={featureTitleSx}>
                Attendance Insights at a Glance
              </Typography>
              <Typography sx={{ ...bodySx, mb: 3 }}>
                Get a clear view of daily absentees across every grade and section. Instantly identify patterns, spot irregularities,
                and take timely action to improve overall attendance and accountability.
              </Typography>
              <CheckList
                items={[
                  "Grade & section wise absentee tracking",
                  "Total absentee count for quick decisions",
                  "Helps schools respond faster and maintain discipline",
                ]}
              />
            </FeatureRow>

            {/* Row 3 */}
            <FeatureRow reverse visual={<FeeCollectionByGradeChart />}>
              <Typography variant="h3" sx={featureTitleSx}>
                Revenue & Outstanding Overview
              </Typography>
              <Typography sx={{ ...bodySx, mb: 3 }}>
                Visualize grade-wise fee collection and pending dues in a single, easy-to-read chart. Understand where revenue is strong
                and where follow-up is needed to ensure healthy cash flow.
              </Typography>
              <CheckList
                items={[
                  "Compare collected vs outstanding by grade",
                  "Detect gaps and prioritize recovery efforts",
                  "Make data-driven financial decisions with confidence",
                ]}
              />
            </FeatureRow>
          </Box>
        </Container>
      </Box>

      {/* Grade wise revenue and outstanding */}

    </>
  );
}

// -------------------- Trust --------------------
function TrustSection() {
  const stats = [
    "500+ Schools",
    "100,000+ Students",
    "99.9% Uptime",
    "10+ Years Experience",
  ];

  return (
    <Container sx={{ py: 6 }}>
      <Grid container spacing={3}>
        {stats.map((s) => (
          <Grid key={s}>
            <Card sx={{ textAlign: "center", py: 3 }}>
              <Typography variant="h5" fontWeight={700} padding={1}>{s}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
// -------------------- Modules --------------------
function ModulesSection() {
  const modules = [
    {
      title: "Admissions Management",
      desc: "Streamline the admissions process, saving time, automating application handling, while providing a smooth and efficient experience for both applicants and administrators.",
      icon: <PersonAddAlt1RoundedIcon />,
    },
    {
      title: "Student Information System",
      desc: "Efficiently manage comprehensive student profiles, including personal details, academic records, and fee information, ensuring all essential data is organized, easily accessible, and up-to-date.",
      icon: <BadgeRoundedIcon />,
    },
    {
      title: "Attendance Tracking",
      desc: "Simplify the attendance marking process for students, notify absentee parents, and generate comprehensive reports with ease. Faculty attendance submission is also seamlessly integrated.",
      icon: <EventAvailableRoundedIcon />,
    },
    {
      title: "Fees & Accounting",
      desc: "Create fee structures, track dues, generate receipts, and seamlessly integrate with online payment systems for accepting payments from parents. Additionally, it can send notifications about outstanding fees to keep parents informed.",
      icon: <CurrencyRupeeRoundedIcon />,
    },
    {
      title: "Examinations & Results",
      desc: "Effortlessly plan and schedule exams, input marks, and generate detailed report cards with ease. Streamline the exam process for both faculty and students while ensuring accurate, real-time results.",
      icon: <AssignmentRoundedIcon />,
    },
    {
      title: "Parent & Staff Communication",
      desc: "Effortlessly send notices, messages, and important updates in real-time, ensuring seamless communication between parents, staff, and administrators.",
      icon: <ForumRoundedIcon />,
    },
  ];

  return (
    <Box sx={{ py: sectionPy, backgroundColor: TINT_BG }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          textAlign="center"
          sx={{ ...sectionHeadingSx, maxWidth: 720, mx: "auto" }}
        >
          {/* Everything Your School Can Manage With MyCampusPilot */}
          {/* MyCampusPilot Capabilities (and More) */}
          Core Features of MyCampusPilot (And Beyond)
        </Typography>

        <Grid container spacing={3} sx={{ mt: { xs: 5, md: 7 } }}>
          {modules.map((m) => (
            <Grid key={m.title} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card elevation={0} sx={cardSx}>
                <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                  <Box sx={{ ...iconBadgeSx, mb: 2.5 }}>{m.icon}</Box>
                  <Typography variant="h3" sx={{ fontSize: "1.15rem", fontWeight: 700, color: "#0f172a", mb: 1 }}>
                    {m.title}
                  </Typography>
                  <Typography sx={{ fontSize: "0.95rem", lineHeight: 1.7, color: "text.secondary" }}>
                    {m.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box >
  );
}

function ScreenshotsSection() {

  const shots = [
    { title: "Revenue/Outstanding Overview", img: "../assets/screenshots/gradeWiseROutstanding.png" },
    { title: "Attendance & Reports", img: "../assets/screenshots/attendanceReport.png" },
  ];

  return (
    <Box sx={{ py: 8 }}>
      <Container>
        <Typography variant="h4" fontWeight={800} textAlign="center">
          See My Campus Pilot In Action
        </Typography>
        <Typography textAlign="center" color="text.secondary" sx={{ mb: 5 }}>
          Real screens from the system used by schools every day.
        </Typography>

        <Grid container spacing={4}>
          {shots.map((s) => (
            <Grid key={s.title}>
              <Card sx={{ p: 2 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  {s.title}
                </Typography>
                <Box
                  component="img"
                  src={require("../assets/screenshots/attendanceReport.png")}
                  sx={{
                    width: "150px",
                    height: "200px",
                    borderRadius: 2,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// -------------------- How It Works --------------------
function HowItWorks() {
  const steps = ["Setup Your School", "Import Students", "Start Managing Digitally"];

  return (
    <Box sx={{ py: sectionPy, backgroundColor: "#ffffff" }}>
      <Container maxWidth="lg" sx={{ textAlign: "center" }}>
        <Typography variant="h2" sx={sectionHeadingSx}>
          How It Works
        </Typography>

        <Box sx={{ position: "relative", mt: { xs: 5, md: 7 } }}>
          {/* Connector behind the step numbers, desktop only */}
          <Box
            aria-hidden
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              top: 56,
              left: "16.66%",
              right: "16.66%",
              borderTop: "2px dashed #cfe0f5",
            }}
          />
          <Grid container spacing={3}>
            {steps.map((s, i) => (
              <Grid key={s} size={{ xs: 12, md: 4 }}>
                <Card elevation={0} sx={{ ...cardSx, position: "relative", p: { xs: 3, md: 4 }, bgcolor: "transparent", border: "none", boxShadow: "none", "&:hover": {} }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      mx: "auto",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "1.4rem",
                      fontWeight: 800,
                      background: "linear-gradient(135deg, #1976d2, #115293)",
                      boxShadow: "0 0 0 8px #ffffff, 0 10px 24px rgba(25, 118, 210, 0.3)",
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography variant="h3" sx={{ fontSize: "1.2rem", fontWeight: 700, color: "#0f172a", mt: 2.5 }}>
                    {s}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

// -------------------- Why Us --------------------
function WhyUs() {
  // Wording unchanged; the leading ✔ is now drawn as an icon.
  const reasons = [
    "Simple & Easy to Use",
    "Built for Real School Workflow",
    "Fast Setup & Training",
    "Secure & Reliable",
    "Works on Mobile, Tablet & Desktop",
    "Dedicated Support Team",
  ];

  return (
    <Box sx={{ py: sectionPy, backgroundColor: TINT_BG }}>
      <Container maxWidth="lg">
        <Typography variant="h2" textAlign="center" sx={sectionHeadingSx}>
          Why MyCampusPilot?
        </Typography>
        {/* Phones: one compact card with divider lines between rows (six separate tiles felt long).
            Tablet and up: a grid of separate tiles. */}
        <Box
          sx={{
            mt: { xs: 5, md: 7 },
            bgcolor: { xs: "#ffffff", sm: "transparent" },
            border: { xs: "1px solid #e5e7eb", sm: "none" },
            borderRadius: { xs: 3, sm: 0 },
            overflow: "hidden",
          }}
        >
          <Grid container spacing={{ xs: 0, sm: 2.5 }}>
            {reasons.map((r, i) => (
              <Grid key={r} size={{ xs: 12, sm: 6, md: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, sm: 2 },
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.5, sm: 2.5 },
                    height: "100%",
                    bgcolor: "#ffffff",
                    borderRadius: { xs: 0, sm: 3 },
                    border: { xs: "none", sm: "1px solid #e5e7eb" },
                    borderBottom: { xs: i < reasons.length - 1 ? "1px solid #eef2f6" : "none", sm: "1px solid #e5e7eb" },
                  }}
                >
                  <Box sx={{ ...iconBadgeSx, width: { xs: 34, sm: 44 }, height: { xs: 34, sm: 44 }, "& svg": { fontSize: { xs: 20, sm: 24 } } }}>
                    <CheckCircleRoundedIcon />
                  </Box>
                  <Typography sx={{ fontSize: { xs: "0.95rem", md: "1.05rem" }, fontWeight: 600, color: "#0f172a" }}>{r}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

// -------------------- Final CTA --------------------
function FinalCTA() {
  const [openDemo, setOpenDemo] = useState(false);
  return (
    <>
      <RequestDemoDialog open={openDemo} onClose={() => setOpenDemo(false)} />
      <Box sx={{ py: sectionPy, backgroundColor: "#ffffff" }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 6, md: 8 },
              px: 3,
              borderRadius: 4,
              color: "#fff",
              background: "linear-gradient(135deg, #1976d2, #0d3c78)",
              boxShadow: "0 24px 60px rgba(25, 118, 210, 0.25)",
            }}
          >
            <Typography
              variant="h2"
              sx={{ ...sectionHeadingSx, color: "#fff" }}
            >
              Ready to Digitize Your School?
            </Typography>

            <Typography
              sx={{ mt: 1.5, fontSize: { xs: "0.975rem", md: "1.1rem" }, color: "rgba(255,255,255,0.85)" }}
            >
              Join hundreds of schools managing everything in one place.
            </Typography>

            <Box
              sx={{
                mt: 4,
                display: "flex",
                gap: 2,
                width: "100%",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => setOpenDemo(true)}
                sx={{
                  bgcolor: "#fff",
                  color: "primary.dark",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 2,
                  px: 4,
                  "&:hover": { bgcolor: "#f1f5f9" },
                }}
              >
                Request Free Demo
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}

// -------------------- Footer --------------------
function Footer() {
  return (
    <Box sx={{ py: { xs: 4, md: 5 }, backgroundColor: "#0f172a", color: "rgba(255,255,255,0.7)" }}>
      <Container maxWidth="lg">
        <Typography textAlign="center" sx={{ fontSize: "0.9rem" }}>
          © {new Date().getFullYear()} MyCampusPilot. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}
